import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Minus, Square, X } from 'lucide-react';

const isDesktopRuntime = typeof window !== 'undefined' && !!window.desktopAPI;

const DesktopChrome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);

  const isMac = meta?.platform === 'darwin';
  const isWindows = meta?.platform === 'win32';
  const platformClass = isMac
    ? 'xph-desktop-titlebar-mac'
    : isWindows
      ? 'xph-desktop-titlebar-win'
      : 'xph-desktop-titlebar-linux';
  const shouldShow = isDesktopRuntime && !!meta;

  useEffect(() => {
    if (!isDesktopRuntime) return undefined;
    let mounted = true;
    document.body.classList.add('xph-desktop-chrome-enabled');
    window.desktopAPI.getMeta().then((payload) => {
      if (mounted) setMeta(payload || { isDesktop: true });
    }).catch(() => {
      if (mounted) setMeta({ isDesktop: true });
    });
    const unsubWindow = window.desktopAPI.onWindowStateChanged?.((payload) => {
      setIsMaximized(Boolean(payload?.isMaximized));
    });
    const unsubUpdate = window.desktopAPI.onUpdateStatusChanged?.((payload) => {
      setUpdateStatus(payload || null);
      if (payload?.status === 'ready-to-install') {
        toast.success('Update downloaded. Ready to install.');
      } else if (payload?.status === 'error' && payload?.message) {
        toast.error(payload.message);
      }
    });
    const unsubMenuNavigate = window.desktopAPI.onMenuNavigate?.((payload) => {
      const targetPath = payload?.path;
      if (!targetPath || typeof targetPath !== 'string') return;
      if (payload?.analysis) {
        window.dispatchEvent(new CustomEvent('desktop:select-analysis', { detail: { analysis: payload.analysis } }));
      }
      if (payload?.dataTarget) {
        window.dispatchEvent(new CustomEvent('desktop:open-datahub', { detail: { target: payload.dataTarget } }));
      }
      navigate(targetPath);
    });
    const unsubMenuAction = window.desktopAPI.onMenuAction?.((payload) => {
      if (payload?.action === 'toggle-theme') {
        window.dispatchEvent(new CustomEvent('desktop:toggle-dashboard-theme'));
      }
    });
    return () => {
      mounted = false;
      document.body.classList.remove('xph-desktop-chrome-enabled');
      if (typeof unsubWindow === 'function') unsubWindow();
      if (typeof unsubUpdate === 'function') unsubUpdate();
      if (typeof unsubMenuNavigate === 'function') unsubMenuNavigate();
      if (typeof unsubMenuAction === 'function') unsubMenuAction();
    };
  }, [navigate]);

  const runWindowAction = async (action) => {
    if (!isDesktopRuntime) return;
    try {
      const result = await window.desktopAPI.windowAction(action);
      if (typeof result?.isMaximized === 'boolean') setIsMaximized(result.isMaximized);
    } catch {
      // Non-blocking
    }
  };

  if (!shouldShow) return null;

  return (
    <>
      <div className={`xph-desktop-titlebar desktop-drag-region ${platformClass}`}>
        <div className="xph-desktop-left">
          {!isMac && <span className="xph-desktop-dot" />}
          <span className="xph-desktop-brand">Intelligence Navigator</span>
          <span className="xph-desktop-route">{location.pathname}</span>
          {updateStatus?.status === 'downloading' && (
            <span className="xph-desktop-route">{`update ${Math.round(updateStatus?.progress?.percent || 0)}%`}</span>
          )}
          {updateStatus?.status === 'ready-to-install' && (
            <span className="xph-desktop-route">update ready</span>
          )}
        </div>
        <div className="xph-desktop-center-title">
          <span className="xph-desktop-brand">Intelligence Navigator</span>
          <span className="xph-desktop-route">{location.pathname}</span>
        </div>
        <div className="xph-desktop-drag-spacer" />
        {!isMac && (
          <div className="xph-desktop-window-controls desktop-no-drag">
            <button onClick={() => runWindowAction('minimize')} title="Minimize"><Minus className="w-3.5 h-3.5" /></button>
            <button onClick={() => runWindowAction('toggle-maximize')} title="Maximize / Restore"><Square className="w-3.5 h-3.5" data-max={isMaximized ? 'yes' : 'no'} /></button>
            <button className="xph-close" onClick={() => runWindowAction('close')} title="Close"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
    </>
  );
};

export default DesktopChrome;
